/**
 * Canvas pagination.
 *
 * ############################################################
 * Canvas paginates with the HTTP `Link` header, NOT with page-number
 * query parameters. Code that requests `?page=1` and stops gets the
 * first 10 items and no indication that anything is missing.
 *
 * That silent truncation at 10 items is the single most common Canvas
 * integration bug. Every list endpoint in this app goes through
 * `fetchAllPages` below — there is no second path.
 * ############################################################
 *
 * A `Link` header looks like:
 *
 *   <https://host/api/v1/courses?page=2&per_page=50>; rel="next",
 *   <https://host/api/v1/courses?page=1&per_page=50>; rel="first",
 *   <https://host/api/v1/courses?page=9&per_page=50>; rel="last"
 *
 * Note that reading it from JavaScript in the browser requires the proxy to
 * name `Link` in `Access-Control-Expose-Headers` — CORS hides it otherwise.
 * The worker does (see worker/handler.ts) and there is a test for it.
 */

export type LinkRel = 'current' | 'next' | 'prev' | 'first' | 'last';

export type ParsedLinks = Partial<Record<LinkRel, string>>;

/**
 * Parse a Canvas `Link` header into `{ next, prev, first, last, current }`.
 *
 * Tolerant by design: unknown rels are ignored, whitespace is flexible, quotes
 * around the rel value are optional, and a malformed segment is skipped rather
 * than throwing. A pagination parser that throws on a weird header turns a
 * cosmetic server quirk into a blank screen.
 */
export function parseLinkHeader(header: string | null | undefined): ParsedLinks {
  if (!header) return {};

  const links: ParsedLinks = {};

  // Split on commas that separate segments, not commas inside <...> URLs.
  for (const segment of header.split(/,\s*(?=<)/)) {
    const match = /^\s*<([^>]*)>\s*(.*)$/.exec(segment);
    if (!match) continue;

    const url = match[1];
    const params = match[2] ?? '';
    if (!url) continue;

    const relMatch = /(?:^|;)\s*rel\s*=\s*"?([^";]+)"?/i.exec(params);
    const rel = relMatch?.[1]?.trim().toLowerCase();
    if (rel === 'next' || rel === 'prev' || rel === 'first' || rel === 'last' || rel === 'current') {
      links[rel] = url.trim();
    }
  }

  return links;
}

/** Convenience for the common case. */
export function nextPageUrl(header: string | null | undefined): string | null {
  return parseLinkHeader(header).next ?? null;
}

export interface PageResult<T> {
  items: T[];
  links: ParsedLinks;
  /** Raw response headers, so callers can read rate-limit info. */
  headers: Headers;
}

export interface FetchAllOptions {
  /**
   * Hard cap on pages followed, so a server-side pagination loop can never
   * hang the app. 100 pages at the default per_page of 100 is 10,000 items —
   * far beyond anything a student course contains.
   */
  maxPages?: number;
  /** Called after each page, for progressive rendering or logging. */
  onPage?: (items: unknown[], pageNumber: number) => void;
  signal?: AbortSignal;
}

/**
 * Follow `rel="next"` until Canvas stops offering one, concatenating results.
 *
 * `fetchPage` is injected rather than imported so this stays a pure function of
 * its inputs and is trivially testable — which matters, because this is the
 * function whose failure mode is invisible.
 */
export async function fetchAllPages<T>(
  firstUrl: string,
  fetchPage: (url: string, signal?: AbortSignal) => Promise<PageResult<T>>,
  options: FetchAllOptions = {},
): Promise<T[]> {
  const maxPages = options.maxPages ?? 100;
  const all: T[] = [];
  const seen = new Set<string>();

  let url: string | null = firstUrl;
  let pageNumber = 0;

  while (url && pageNumber < maxPages) {
    // A server that points `next` back at a page we already fetched would loop
    // forever. Canvas should not, but "should not" is not a guarantee.
    if (seen.has(url)) break;
    seen.add(url);

    const page: PageResult<T> = await fetchPage(url, options.signal);
    pageNumber += 1;
    all.push(...page.items);
    options.onPage?.(page.items, pageNumber);

    const next: string | undefined = page.links.next;
    url = next && next !== url ? next : null;
  }

  return all;
}

/* ------------------------------------------------------------------ */
/* Rate limiting                                                       */
/* ------------------------------------------------------------------ */

/**
 * Canvas reports a leaky-bucket quota in `X-Rate-Limit-Remaining`, which starts
 * at 700 and falls as requests cost against it. At zero, Canvas answers 403 with
 * a "Rate Limit Exceeded" body.
 *
 * The rule from BUILD_PROMPT — do not fan out 40 parallel requests on page load,
 * batch and back off — is enforced in two places: this tracker, and the
 * concurrency limiter in `client.ts`.
 */
export class RateLimitTracker {
  /** Canvas's documented starting quota. */
  private remaining = 700;
  private lastCost = 0;

  /** Below this, start slowing down rather than waiting for the 403. */
  static readonly LOW_WATER = 150;
  /** Below this, back off hard. */
  static readonly CRITICAL = 50;

  observe(headers: Headers): void {
    // `Number(null)` is 0, not NaN — so reading an absent header straight into
    // Number() would report a fully exhausted quota and make every subsequent
    // request sleep five seconds. Check for the header's presence first.
    const rawRemaining = headers.get('X-Rate-Limit-Remaining');
    if (rawRemaining !== null) {
      const remaining = Number(rawRemaining);
      if (Number.isFinite(remaining)) this.remaining = remaining;
    }

    const rawCost = headers.get('X-Request-Cost');
    if (rawCost !== null) {
      const cost = Number(rawCost);
      if (Number.isFinite(cost)) this.lastCost = cost;
    }
  }

  get quotaRemaining(): number {
    return this.remaining;
  }

  get costOfLastRequest(): number {
    return this.lastCost;
  }

  /** How long to wait before the next request, in milliseconds. */
  delayMs(): number {
    if (this.remaining <= 0) return 5_000;
    if (this.remaining < RateLimitTracker.CRITICAL) return 2_000;
    if (this.remaining < RateLimitTracker.LOW_WATER) return 500;
    return 0;
  }

  /** How many requests it is reasonable to have in flight right now. */
  concurrency(): number {
    if (this.remaining < RateLimitTracker.CRITICAL) return 1;
    if (this.remaining < RateLimitTracker.LOW_WATER) return 2;
    return 4;
  }

  reset(): void {
    this.remaining = 700;
    this.lastCost = 0;
  }
}
